import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { useLocation } from '@reach/router';

import {
  challengeMetaSelector,
  challengeTestsSelector,
  challengeFilesSelector
} from '../../templates/Challenges/redux/selectors';

import useHints from './use-hints';
import { renderMarkdown } from './markdown';
import { MAX_HINT_LEVEL } from './hint-service';
import chatbotIcon from './AI chatbot icon.png';
import reloadIcon from './ci_arrow-reload-02.png';
import closeIcon from './carbon_close-outline.png';
import './chatbot.css';

// ────────────────────────────────────────────
// Routes where the chatbot should NOT appear.
// Each entry is a certification slug (the segment after /learn/).
// e.g. 'relational-database' excludes /learn/relational-database/**
// ────────────────────────────────────────────
const EXCLUDED_ROUTES = [
  'relational-database'
];

// ────────────────────────────────────────────
// Helper: determine if chatbot should show on the current path
// Only show on challenge pages: /learn/:cert/:block/:challenge
// ────────────────────────────────────────────
function shouldShowChatbot(pathname) {
  if (!pathname) return false;
  const segments = pathname.replace(/^\//, '').replace(/\/$/, '').split('/');
  // Must be /learn/:cert/:block/:challenge (4 segments)
  if (segments.length < 4 || segments[0] !== 'learn') return false;
  const certification = segments[1];
  if (EXCLUDED_ROUTES.includes(certification)) return false;
  return true;
}

// ────────────────────────────────────────────
// Helper: extract code from Redux challenge files
// ────────────────────────────────────────────
function getCodeFromFiles(challengeFiles) {
  if (!challengeFiles || challengeFiles.length === 0) return '';
  return challengeFiles
    .map(f => f.contents || f.editableContents || '')
    .filter(Boolean)
    .join('\n\n');
}

// ────────────────────────────────────────────
// Component
// ────────────────────────────────────────────
function Chatbot({ challengeMeta, challengeTests, challengeFiles }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [visible, setVisible] = useState(false);
  const bodyRef = useRef(null);

  const {
    hints,
    currentLevel,
    maxLevel,
    loading,
    error,
    requestHint,
    resetHints,
    apiKey
  } = useHints();

  // Show FAB after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      const scrollY = window.scrollY;
      document.body.style.setProperty('--chatbot-scroll-y', `-${scrollY}px`);
      document.body.classList.add('chatbot-open');
    } else {
      const scrollY = document.body.style.getPropertyValue('--chatbot-scroll-y');
      document.body.classList.remove('chatbot-open');
      document.body.style.removeProperty('--chatbot-scroll-y');
      window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
    }
    return () => {
      document.body.classList.remove('chatbot-open');
      document.body.style.removeProperty('--chatbot-scroll-y');
    };
  }, [open]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [hints, loading]);

  // Reset hints when the challenge changes
  const prevChallengeId = useRef(challengeMeta?.id);
  useEffect(() => {
    if (challengeMeta?.id && challengeMeta.id !== prevChallengeId.current) {
      prevChallengeId.current = challengeMeta.id;
      resetHints();
    }
  }, [challengeMeta?.id, resetHints]);

  // Close on Escape
  useEffect(() => {
    const handleEsc = e => {
      if (e.key === 'Escape' && open) handleClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [open]);

  // Close with animation
  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      setOpen(false);
      setClosing(false);
    }, 250);
  }, []);

  const handleToggle = useCallback(() => {
    if (open) {
      handleClose();
    } else {
      setOpen(true);
    }
  }, [open, handleClose]);

  // ---- Request a hint ----
  const handleRequestHint = useCallback(() => {
    const title = challengeMeta?.title || '';
    const description = challengeMeta?.description || '';
    const instructions = challengeMeta?.instructions || '';
    const tests = challengeTests || [];
    const code = getCodeFromFiles(challengeFiles);

    requestHint({ title, description, instructions, tests, code });
  }, [challengeMeta, challengeTests, challengeFiles, requestHint]);

  const allHintsUsed = hints.length >= maxLevel;
  const progressPct = (hints.length / maxLevel) * 100;

  // ────────── Render ──────────
  if (!visible || !shouldShowChatbot(location.pathname)) return null;

  return (
    <>
      {/* Backdrop */}
      {(open || closing) && (
        <div
          className={`chatbot-backdrop${closing ? ' closing' : ''}`}
          onClick={handleClose}
          aria-hidden='true'
        />
      )}

      {/* Floating action button */}
      <button
        className={`chatbot-fab${open ? ' is-open' : ''}`}
        onClick={handleToggle}
        aria-label={open ? 'Close hint assistant' : 'Open hint assistant'}
        title='AI Hint Assistant'
      >
        <img
          src={chatbotIcon}
          alt='AI Chatbot'
          className='chatbot-fab-icon'
        />
      </button>

      {/* Modal panel */}
      {(open || closing) && (
        <div
          className={`chatbot-panel${closing ? ' closing' : ''}`}
          role='dialog'
          aria-label='Hint assistant'
        >
          {/* ── Header ── */}
          <div className='chatbot-header'>
            <div className='chatbot-header-left'>
              <img
                src={chatbotIcon}
                alt=''
                className='chatbot-header-icon'
              />
              <div>
                <h3 className='chatbot-header-title'>Need Help?</h3>
                <div className='chatbot-header-subtitle'>
                  AI-powered smart hints
                </div>
              </div>
            </div>
            <div className='chatbot-header-actions'>
              <button
                className='chatbot-header-btn'
                onClick={resetHints}
                title='Reset hints'
              >
                <img src={reloadIcon} alt='Reset' className='chatbot-btn-img' />
              </button>
              <button
                className='chatbot-header-btn'
                onClick={handleClose}
                title='Close'
              >
                <img src={closeIcon} alt='Close' className='chatbot-btn-img' />
              </button>
            </div>
          </div>

          {/* ── Body ── */}
          <div className='chatbot-body' ref={bodyRef}>
            {/* Empty state */}
            {hints.length === 0 && !loading && !error && (
              <div className='chatbot-empty'>
                <div className='chatbot-empty-icon'>
                  <img
                    src={chatbotIcon}
                    alt=''
                    className='chatbot-empty-img'
                  />
                </div>
                <div>
                  Stuck on this challenge?
                  <br />
                  Click <strong>&quot;Give me a hint&quot;</strong> below.
                  <br />
                  Each click reveals a progressively
                  <br />
                  more detailed hint!
                </div>
              </div>
            )}

            {/* Hint cards */}
            {hints.map((hint, idx) => {
              return (
                <div className='chatbot-hint' key={idx}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: renderMarkdown(hint.text)
                    }}
                  />
                </div>
              );
            })}

            {/* Loading indicator */}
            {loading && (
              <div className='chatbot-loading'>
                <div className='chatbot-typing-dots'>
                  <span />
                  <span />
                  <span />
                </div>
                Generating your hint…
              </div>
            )}

            {/* Error */}
            {error && <div className='chatbot-error'>⚠️ {error}</div>}
          </div>

          {/* ── Footer ── */}
          <div className='chatbot-footer'>
            {/* Progress bar */}
            <div className='chatbot-level-bar'>
              <span>
                {hints.length}/{maxLevel} hints
              </span>
              <div className='chatbot-progress-track'>
                <div
                  className='chatbot-progress-fill'
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>

            {allHintsUsed ? (
              <div className='chatbot-max-reached'>
                You&apos;ve used all available hints for this challenge.
              </div>
            ) : (
              <button
                className='chatbot-hint-btn'
                onClick={handleRequestHint}
                disabled={loading || !apiKey}
              >
                {loading
                  ? 'Generating…'
                  : hints.length === 0
                    ? 'Give me a hint'
                    : 'Give me a more detailed hint'}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

Chatbot.displayName = 'Chatbot';

// ────────────────────────────────────────────
// Redux connection
// ────────────────────────────────────────────
const mapStateToProps = createSelector(
  challengeMetaSelector,
  challengeTestsSelector,
  challengeFilesSelector,
  (challengeMeta, challengeTests, challengeFiles) => ({
    challengeMeta: challengeMeta || {},
    challengeTests: challengeTests || [],
    challengeFiles: challengeFiles || []
  })
);

export default connect(mapStateToProps)(Chatbot);
